import { ATRIBUTOS, type Pontos } from './escala'
import { NIVEIS_ZERO, niveisGastos, pontosComNiveis, type Niveis } from './niveis'
import type { PerfilDeTaco, Ruleset } from './ruleset'
import { variarComSeed } from './seed'

/**
 * Distribui um orçamento pelos atributos na proporção dos pesos, com teto.
 *
 * Em duas fases porque o teto muda a conta: um atributo que pediria 150 fica
 * em 100, e os 50 que sobram vão para os OUTROS, na proporção deles — senão o
 * orçamento não fecharia e a raridade valeria menos do que promete. Repete
 * até ninguém passar do teto (no máximo uma volta por atributo).
 *
 * O arredondamento é por maior resto: arredondar cada um sozinho perde ou
 * inventa pontos, e a soma tem de bater exatamente com o orçamento — é o que
 * o teste de "nenhum arquétipo domina" e o de orçamento comparam.
 */
export function distribuir(orcamento: number, pesos: Pontos, teto = 100): Pontos {
  const alvo = Math.min(orcamento, teto * ATRIBUTOS.length)
  const fixo: Partial<Record<keyof Pontos, number>> = {}
  let restante = alvo

  for (let volta = 0; volta < ATRIBUTOS.length; volta++) {
    const livres = ATRIBUTOS.filter((k) => fixo[k] === undefined)
    const somaPesos = livres.reduce((s, k) => s + pesos[k], 0)
    if (livres.length === 0 || somaPesos === 0) break

    let estourou = false
    for (const k of livres) {
      const ideal = (restante * pesos[k]) / somaPesos
      if (ideal > teto) {
        fixo[k] = teto
        restante -= teto
        estourou = true
      }
    }
    if (!estourou) break
  }

  // Arredondamento por maior resto entre os que sobraram livres.
  const livres = ATRIBUTOS.filter((k) => fixo[k] === undefined)
  const somaPesos = livres.reduce((s, k) => s + pesos[k], 0)

  // Sem peso nenhum entre os livres não há proporção pra seguir: "dividir
  // igualmente" inventaria pontos que ninguém pediu. O restante fica SEM
  // DONO (zeros ali), em vez de ser espalhado — o caso não ocorre no
  // RULESET_V3 (todo perfil tem ao menos um peso positivo), mas `distribuir`
  // é exportada e a Task 3 constrói em cima dela.
  const chao = somaPesos === 0 ? livres.map(() => 0) : (() => {
    const exatos = livres.map((k) => (restante * pesos[k]) / somaPesos)
    const base = exatos.map(Math.floor)
    let faltam = restante - base.reduce((s, v) => s + v, 0)
    const ordem = exatos
      .map((v, i) => ({ i, resto: v - base[i]! }))
      .sort((a, b) => b.resto - a.resto)
    for (const { i } of ordem) {
      if (faltam <= 0) break
      if (base[i]! < teto) { base[i]! += 1; faltam -= 1 }
    }
    return base
  })()

  const fora = { ...fixo } as Record<keyof Pontos, number>
  livres.forEach((k, i) => { fora[k] = chao[i]! })
  return { peso: fora.peso ?? 0, efeito: fora.efeito ?? 0, aderencia: fora.aderencia ?? 0, quebra: fora.quebra ?? 0 }
}

/**
 * Os pontos de um taco: orçamento da raridade na direção do arquétipo, com a
 * variação da seed e, por cima, os níveis comprados.
 *
 * Os níveis entram DEPOIS da seed, de propósito: a seed varia o taco dentro
 * do orçamento da raridade (soma constante), e o nível é o único que soma
 * pontos ao total. Misturar os dois faria o upgrade se diluir na variação.
 */
export function pontosDoTaco(perfil: PerfilDeTaco, ruleset: Ruleset, niveis: Niveis = NIVEIS_ZERO): Pontos {
  const pesos = ruleset.perfil[perfil.arquetipo]
  const base = distribuir(ruleset.orcamento[perfil.raridade], pesos, ruleset.tetoPorAtributo)
  const comSeed = variarComSeed(base, pesos, perfil.seed, ruleset.amplitudeDaSeed)
  return niveisGastos(niveis) > 0 ? pontosComNiveis(comSeed, niveis, ruleset) : comSeed
}
